import { useCallback, useEffect, useRef } from 'react';

const TYPING_START_THROTTLE_MS = 2000;
const TYPING_STOP_IDLE_MS = 3000;

type EmitTyping = (conversationId: string) => void;

export const useTypingEmitter = (
  conversationId: string | undefined,
  emitTypingStart: EmitTyping,
  emitTypingStop: EmitTyping
) => {
  const lastStartAtRef = useRef(0);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearStopTimer = () => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  };

  const stopTyping = useCallback(() => {
    if (!conversationId) return;
    clearStopTimer();
    emitTypingStop(conversationId);
  }, [conversationId, emitTypingStop]);

  const notifyTyping = useCallback(
    (text: string) => {
      if (!conversationId) return;

      if (!text.trim()) {
        stopTyping();
        return;
      }

      const now = Date.now();
      if (now - lastStartAtRef.current >= TYPING_START_THROTTLE_MS) {
        emitTypingStart(conversationId);
        lastStartAtRef.current = now;
      }

      clearStopTimer();
      stopTimerRef.current = setTimeout(stopTyping, TYPING_STOP_IDLE_MS);
    },
    [conversationId, emitTypingStart, stopTyping]
  );

  useEffect(() => {
    return () => {
      stopTyping();
    };
  }, [stopTyping]);

  return { notifyTyping, stopTyping };
};

export const TYPING_PREVIEW = 'Typing...';
